import { forwardRef, useImperativeHandle } from 'react';

import { BaseTabProps, FeatureTabRef, useBaseTab } from '@/modules/common';

import { BaseTabFormData, SubmitDateRange } from '../types/types';

/**
 * Props for BaseFeatureTab component
 */
export interface BaseFeatureTabProps<TData, TFormData extends BaseTabFormData = BaseTabFormData>
  extends BaseTabProps<TData, TFormData> {
  /**
   * The main content of the tab
   */
  children: React.ReactNode;

  /**
   * Function to validate the form (called on submit)
   * Receives optional date range for conflict detection in Edit/EditChange modes
   */
  onSubmit?: (dateRange?: SubmitDateRange) => Promise<void>;

  /**
   * Whether the form is currently valid
   */
  isValid?: boolean;

  /**
   * Function to get the current form data
   */
  getFormData?: () => TFormData;

  /**
   * Function to determine if entity is archived (for read-only mode)
   */
  isArchivedFn?: (entityData?: TData) => boolean;

  /**
   * Whether the tab is currently checking for conflicts
   * Exposed via ref for parent to read loading state
   */
  isCheckingConflicts?: boolean;
}

/**
 * Generic base component for feature tabs that provides common functionality.
 * Handles mode logic and imperative handle setup for form validation.
 *
 * @example
 * ```typescript
 * export const MyTab = forwardRef<MyTabRef, MyTabProps>((props, ref) => (
 *   <BaseFeatureTab
 *     {...props}
 *     ref={ref}
 *     isArchivedFn={(data) => data?.status === 'archived'}
 *     onSubmit={handleSubmit}
 *     isValid={form.formState.isValid}
 *     getFormData={() => form.getValues()}
 *   >
 *     <Card>
 *       <CardContent>
 *         { Your tab content here }
 *       </CardContent>
 *     </Card>
 *   </BaseFeatureTab>
 * ));
 * ```
 */
export const BaseFeatureTab = forwardRef(
  <TData, TFormData extends BaseTabFormData>(
    {
      children,
      mode,
      featureData,
      draftData,
      onFormStateChange,
      onSubmit = async (_dateRange?: SubmitDateRange) => {},
      isValid = true,
      getFormData = () => ({}) as TFormData,
      isArchivedFn = () => false,
      isCheckingConflicts = false,
    }: BaseFeatureTabProps<TData, TFormData>,
    ref: React.ForwardedRef<FeatureTabRef<TFormData>>
  ) => {
    useBaseTab(
      {
        mode,
        featureData,
        draftData,
        onFormStateChange,
      },
      {
        isArchivedFn,
      }
    );

    // Set up imperative handle for parent components
    // submit receives optional date range for conflict detection
    // isCheckingConflicts exposed for parent to read loading state
    useImperativeHandle(
      ref,
      () => ({
        submit: (dateRange?: SubmitDateRange) => onSubmit(dateRange),
        isValid,
        getFormData: () => getFormData() as TFormData,
        isCheckingConflicts,
      }),
      [onSubmit, isValid, getFormData, isCheckingConflicts]
    );

    return <>{children}</>;
  }
) as <TData, TFormData extends BaseTabFormData>(
  props: BaseFeatureTabProps<TData, TFormData> & {
    ref?: React.ForwardedRef<FeatureTabRef<TFormData>>;
  }
) => React.ReactElement;
